import { AnimatePresence, motion } from "motion/react";
import { readingTime } from "reading-time-estimator";

import receiveSrc from "~/assets/audio/imessage-receive.mp3";
import sendSrc from "~/assets/audio/imessage-send.mp3";
import { useSound } from "~/helpers/sound";
// import { useReactionSrc } from "~/helpers/textingReactions";
import { type PatternsTextingMessage } from "~/types";

import PatternsTypingIndicator from "./PatternsTypingIndicator";

import classes from "./PatternsText.module.css";

export interface PatternsTextProps
  extends Omit<BoxProps, "style" | "children"> {
  message: PatternsTextingMessage;
  from: "us" | "them";
  animate?: boolean;
  onAnimationStart?: () => void;
  onAnimationComplete?: () => void;
}

const MotionLi = motion.create(List.Item);

const PatternsText: FC<PatternsTextProps> = ({
  message,
  from,
  animate,
  onAnimationStart,
  onAnimationComplete,
  className,
  ...otherProps
}) => {
  const [textWidth, setTextWidth] = useState<number | null>(null);
  const [playSound] = useSound(from === "us" ? sendSrc : receiveSrc);
  // const reactionSrc = useReactionSrc(message.reaction, from);
  const [typing, setTyping] = useState(() => animate);
  useEffect(() => {
    if (typing) {
      let duration = message.typing_duration_ms;
      if (!duration && message.body) {
        const { words } = readingTime(message.body);
        duration = Math.min((words / 300) * 60 * 1000, 3000);
      }
      const timeout = setTimeout(() => {
        setTyping(false);
        if (!message.body && onAnimationComplete) {
          setTimeout(onAnimationComplete, 500);
        }
      }, duration ?? 600);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AnimatePresence>
      {typing && <PatternsTypingIndicator {...{ from }} />}
      {!!message.body && !typing && (
        <MotionLi
          classNames={{
            item: cn("PatternsText-root", classes.root, className),
            itemLabel: classes.label,
          }}
          {...(typeof textWidth === "number" && {
            styles: { itemLabel: { width: textWidth + 1 } },
          })}
          mod={{ from /* glowing: message.glowing */ }}
          initial={animate ? "hidden" : "entered"}
          variants={{
            hidden: {
              opacity: 0,
              y: 100,
            },
            entered: {
              opacity: 1,
              y: 0,
              transition: {
                type: "spring",
                stiffness: 200,
                mass: 0.2,
                damping: 20,
              },
            },
          }}
          {...(animate && { animate: "entered" })}
          onAnimationStart={() => {
            playSound();
            onAnimationStart?.();
          }}
          {...{ onAnimationComplete }}
          {...otherProps}
        >
          <SelfMeasuringSpan
            className={classes.message}
            onLayout={setTextWidth}
          >
            {message.body}
          </SelfMeasuringSpan>
          {/* {reactionSrc && (
            <motion.img
              src={reactionSrc}
              className={classes.reaction}
              variants={{
                hidden: { scale: 0, opacity: 0 },
                entered: { scale: 1.2, opacity: 1 },
              }}
              {...(animate
                ? { initial: "hidden", animate: "entered" }
                : { initial: "entered" })}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 20,
                delay: 1.6,
              }}
              {...{ onAnimationComplete }}
            />
          )} */}
        </MotionLi>
      )}
    </AnimatePresence>
  );
};

export default PatternsText;

interface SelfMeasuringSpanProps extends ComponentPropsWithoutRef<"span"> {
  onLayout: (width: number) => void;
}

const SelfMeasuringSpan: FC<SelfMeasuringSpanProps> = ({
  children,
  onLayout,
  ...otherProps
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const span = ref.current;
    if (span) {
      const { width } = span.getBoundingClientRect();
      onLayout(width);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <span ref={ref} {...otherProps}>
      {children}
    </span>
  );
};
